import { Platform } from "react-native";
import axios from "axios";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/react-native";
import { getItem } from "@lib/storage";
import { codeBundleId } from "@lib/sentry";
import { saveFpReuseTtl } from "@lib/fingerprint";
import { baseURL, appVersion, appVersionCode } from "../config";
import {
  onFulfilledRequestInterceptor,
  onFulfilledResponseInterceptor,
  onRejectedResponseInterceptor,
} from "./api-utils";
import getTokenZ from "@utils/getTokenZ";

export const getToken = async () => {
  try {
    return {
      token: await getItem("user_token"),
    };
  } catch (e) {
    Sentry.captureException(e);
  }
};

// The server treats a request without `x-platform-os: WEB` as an Android build and
// applies the min-app-version force-update floor; the rn-web build reports a
// fake Android version code, so it must identify itself. Native builds send nothing.
const webPlatformHeaders =
  Platform.OS === "web" ? { "x-platform-os": "WEB" } : {};

export const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    "x-app-name": "CPDB",
    "x-app-version": appVersion,
    "x-app-version-cp": codeBundleId,
    "x-app-version-code": appVersionCode,
    "use-applinks-cl": "true",
    ...webPlatformHeaders,
  },
});

axiosRetry(api, { retryDelay: axiosRetry.exponentialDelay, retries: 3 });

api.interceptors.request.use(onFulfilledRequestInterceptor);

api.interceptors.response.use(
  onFulfilledResponseInterceptor,
  onRejectedResponseInterceptor,
);

export const getError = () => api.get("/cpdb/get-error");

// api calls start here

// category related apis

export const getAllCategories = () => api.get("/products/all-category-listing");

export const getAllPrimaryCategoriesWithProductTypes = () =>
  api.get("/products/get-category-list");

// product related apis

// export const getProductByCategory = ({ category_url, offset, limit }) =>
//   api.get(
//     `/cpdb/tl-products-new/${category_url}?offset=${offset}&limit=${limit}`,
//   );

export const unSuspendMe = (data) => api.post("/cpdb/unsuspend-me", data);

export const getProductDetails = (slug) => api.get(`/products/${slug}`);

export const getProductFullDetails = (slug) =>
  api.get(`/products/single-product-detail/${slug}`);

export const getProductsBySearch = (searchTerm) =>
  api.post("/cpdb/search-product-new", { product: searchTerm });

export const getLeaderCustomSkuList = (data = {}) =>
  api.post("/cpdb/get-leader-custom-sku-list", data);

export const cancelLeaderCustomSkuOrder = (data = {}) =>
  api.post("/cpdb/cancel-leader-custom-sku-order", data);

export const getLeaderCustomSkuOrders = (data = {}) =>
  api.post("/cpdb/get-leader-custom-sku-orders", data);

export const getLeaderPaymentDetails = (data = {}) =>
  api.post("/cpdb/get-leader-payment-details", data);

export const createLeaderPayment = (data = {}) =>
  api.post("/cpdb/create-custom-order", data);

export const completeLeaderPayment = (data = {}) =>
  api.post("/cpdb/complete-custom-order", data);

export const placeNewOrder = (data = {}) =>
  api.post("/orders/create-order", data);

export const completeOrder = (data = {}) =>
  api.post("/orders/complete-order", data);

export const getAllOrders = ({
  offset = 0,
  limit = 10,
  query = "",
  userId = "",
  date,
  type = "NOT_ASSIGNED",
  vacationMode = false,
}) =>
  api.get(
    `/cpdb/orders-list?offset=${offset}&limit=${limit}&searchTerm=${query}&userId=${userId}&date=${date}&vacationMode=${vacationMode}&type=${type}`,
  );

export const getCustomerOrders = ({
  url,
  user_id = "",
  offset,
  limit,
  query = "",
}) =>
  api.get(
    `/cpdb/order-copy/${url}/${user_id}?offset=${offset}&limit=${limit}&searchTerm=${query}`,
  );

// export const changeNumberRequest = (data) =>
//   api.post("/cpdb/change-number-request", data);

// export const verifyChangeNumberRequest = (data) =>
//   api.post("/cpdb/verify-change-number-request", data);

export const getOrderDetailById = ({ orderId, isVacationOrder }) =>
  api.get(`/cpdb/order-details/${orderId}?isVacationOrder=${isVacationOrder}`);

export const getOrderDetailByIdV2 = ({
  orderId,
  isVacationOrder = false,
  source = "FORWARD",
}) =>
  api.get(
    `/cpdb/order-details-v2/${orderId}?isVacationOrder=${isVacationOrder}&source=${source}`,
  );

export const getSingleOrderDetails = (orderId = "") =>
  api.get(`users/order-details/${orderId}`);

export const orderCancellationReasons = () =>
  api.get("/cpdb/cancellation-reasons");

export const cancelOrder = (data = {}) => {
  return api.post("/cpdb/cancel", data);
};

export const getRatingPlayStore = () => api.get("/cpdb/rating-check"); // TODO - remove this api, handle this using /me

export const giveRatingPlayStore = () =>
  api.post("/cpdb/rating-update", { rating_done: true });

export const shouldShowInAppRating = () =>
  api.get("/cpdb/show-inapp-app-rating");

export const shouldInAppUpdate = (currentVersion = "") =>
  api.get(`/cpdb/show-inapp-update?currentVersion=${currentVersion}`);

// user or team leader related api calls here

export const signUp = (data = {}) => {
  return api.post("/cpdb/sign-up-team-leader", data);
};

export const sendOtpToUser = async (phone_number = "", isDebug = false) => {
  const data = {
    phone_number,
    isDebug,
    trackingInfo: {},
    source: "app",
  };
  const response = await api.post("/cpdb/get-otp-db", data);
  // Reuse window for the cached Fingerprint requestId (see @lib/fingerprint).
  // A 4xx resolved as undefined by the interceptor, or an older backend,
  // leaves the field absent and resets the app to its 5-min fallback.
  saveFpReuseTtl(response?.data?.fp_reuse_ttl_ms);
  return response;
};

export const registerNewNumber = (
  phone_number = "",
  name = "",
  isDebug = true,
  code = "",
  gender = "",
) => {
  const data = {
    phone_number,
    name,
    isDebug,
    code,
    gender,
  };
  return api.post("/cpdb/update-leader-name", data);
};

export const checkClCode = (code = "") => {
  const data = {
    code,
  };
  return api.post("/cpdb/verify-signup-code", data);
};

export const verifyOtpForLogin = async (data = {}, checkbox = false) => {
  if (checkbox) {
    return api.post("/cpdb/verify-db-otp", { ...data, source: "app" });
  }
  return api.post("/cpdb/verify-tl-user", { ...data, source: "app" });
};

export const retryDevice = (data = {}) =>
  api.post("/cpdb/device-verification/retry", data);
export const loginRefresh = ({
  ivrSessionId = "",
  phone_number = "",
  newTcUser = "",
}) =>
  api.get(
    `/cpdb/device-verification/ivr-session-status?ivrSessionId=${ivrSessionId}&phone_number=${phone_number}&newTcUser=${newTcUser}`,
  );

export const getUserDetails = (cache) => {
  const finalUrl = cache ? "/cpdb/me?from=cache" : "/cpdb/me";
  return api.get(finalUrl);
};

export const getTotalPayout = () => api.post("/cpdb/total-sale-payout");

export const getDailySalesStats = () => api.post("/cpdb/team-leader-stats");

export const getTotalDailyPayout = () => api.post("/cpdb/team-leader-payout");

export const addNewProductRequest = (requestProduct) =>
  api.post("cpdb/add-request-product", { requestProduct });

export const getAllProductRequests = () => api.get("/cpdb/request-product");

export const addLeaderContacts = (contacts = {}) =>
  api.post("/cpdb/add-tl-contact", { contacts });

export const isLeaderAlreadyHaveContacts = () =>
  api.get("/cpdb/is-user-have-contacts");

export const leaderReferMessage = () => api.get("/cpdb/refer-message");

export const searchProducts = (searchTerm = "") =>
  api.get(`/cpdb/search/products?searchTerm=${searchTerm}`);

// cart related api calls here
export const setCartItem = (data = {}) => api.post("/cpdb/set-cart-item", data);

export const addToCart = (data = {}) => api.post("/cpdb/add-to-cart", data);
export const removeFromCart = (data = {}) =>
  api.post("/cpdb/remove-from-cart", data);

export const viewCart = ({ userId }) =>
  api.post("/cpdb/view-cart-new", { userId });

export const placeOrder = (data = {}) => api.post("/cpdb/create-order-2", data);

export const completePaymentOld = (data = {}) =>
  api.post("/cpdb/complete-order-2", data);

export const completePayment = (data = {}) =>
  api.post("/cpdb/complete-order-3", data);

export const whatsAppOpt = (whatsapp_opt = false) => {
  return api.post("/cpdb/opt-whatsapp", { whatsapp_opt });
};

export const applyCouponCode = (data = {}) =>
  api.post("/cpdb/apply-coupon", data);

export const removeCouponCode = (data = {}) =>
  api.post("/cpdb/remove-coupon", data);

export const createTlUser = (data = {}) =>
  api.post("cpdb/create-tl-user", data.userCreds);

export const editTlUser = ({ user_id = "", userData }) =>
  api.post(`cpdb/edit-tl-contact/${user_id}`, userData);

export const deleteClUser = (user_id = "", selected) =>
  api.delete(`cpdb/delete-cl-user/${user_id}?reason=${selected}`);

export const getAllTemLeaders = () => api.get("cpdb/view-team-leader");

// export const getAllTeamLeaderUsers = () => api.get(`cpdb/users`);
// old_one_gagan_commented
// export const getAllTeamLeaderUsers = ({ limit, offset, query = "" }) =>
//   api.get(
//     `cpdb/users-copy?limit=${limit}&offset=${offset}&searchTerm=${query}`,
//   );
export const getAllTeamLeaderUsers = () => api.get("cpdb/all-customers");

export const getTlUser = (user_id = "") => api.get(`cpdb/users-new/${user_id}`);

export const getAllLeaderCustomers = ({
  limit,
  offset,
  query = "",
  cohortId,
}) =>
  api.get(
    `cpdb/customers?cohort_id=${cohortId}&limit=${limit}&offset=${offset}&search_term=${query}`,
  );

export const deleteTeamLeaderUser = ({ userId }) =>
  api.delete(`cpdb/users/${userId}`);

export const getBannerImages = () => api.get("app/get-all-banners");

// PDF Sharing related
export const getPDFOfProducts = () => api.get("products/all-product-pdf");

export const getPDFOfProductByCategory = (url) =>
  api.get(`products/all-cat-product-pdf/${url}`);

// VERSION
export const getAppVersion = () => api.get("cpdb/version");

export const getEmergencyMessage = () => api.get("cpdb/emergency-message");

// Necessary contacts
export const getSupportContacts = () => api.get("cpdb/support-phonenumber");

export const getSupportAndOnboardingNumbers = () =>
  api.get("cpdb/onboarding-support-number");

export const getWAContacts = () => api.get("cpdb/wa-group-phonenumbers");

export const getPincodeByLatLong = (lat = "", long = "") =>
  api.get(`cpdb/reverse-geocode?lat=${lat}&lng=${long}`);

export const increaseOnboardingStatus = () =>
  api.post("cpdb/onboarded-step-one");

export const generatCXLink = (phone_number) =>
  api.get(`cpdb/leader-app-share-link?phoneNumber=${phone_number}`);

// Leader Bank account details
export const manageBankDetails = (details) =>
  api.post("cpdb/edit-tl-bank-account", details);
// support apis

export const getSupportItemOrListOld = ({ id, type }) => {
  return api.get(`support/cl/config?id=${id}&type=${type}`);
};

export const getSupportItemOrList = ({ id, type, item_id }) => {
  return api.get(
    `support/v2/cl/config?id=${id}&type=${type}&item_id=${item_id}`,
  );
};

export const sendReply = (data) => api.post("support/cl/send-reply", data);
export const addTicketRating = (data) =>
  api.post("support/cl/add-rating", data);

export const cancelTicket = (data) =>
  api.post("support/cl/cancel-ticket", data);

export const reopenTicket = (data) =>
  api.post("support/cl/reopen-ticket", data);

export const createNewSupportTicket = (data) =>
  api.post("support/cl/new", data);

export const fetchOpenTickets = () => {
  return api.get("support/cl/get");
};
export const fetchOpenTicketsNew = ({
  limit = 10,
  offset = 0,
  filter = "all",
}) => {
  return api.get(
    `support/cl/get-new?filter=${filter}&limit=${limit}&offset=${offset}`,
  );
};

export const fetchTicketDetails = (id) => {
  return api.get(`support/cl/get-ticket?id=${id}`);
  // return new Promise(resolve => resolve({ data: openTickets[0] }));
};

export const getCouponsForUser = (userId) =>
  api.get(`cpdb/get-coupons?user_id=${userId}`);

export const getLeaderDetails = (id) =>
  api.get(`cpdb/get-leader-details?id=${id}`);

export const updateAlternatePhone = (alternate_phone) =>
  api.post("cpdb/update-alternate-number", { alternate_phone });

export const getAllGroupBuys = () => api.get("/cpdb/all-group-buys");

export const getAllGroupBuysWithOrders = () =>
  api.get("/cpdb/all-group-buys-with-orders");

export const getGroupBuyById = (id) => api.get(`/cpdb/group-buy/${id}`);

export const getGroupBuyCustomers = ({ id, limit, offset, searchTerm }) => {
  return api.get(
    `/cpdb/group-buy/${id}/customers?limit=${limit}&offset=${offset}&searchTerm=${searchTerm}`,
  );
};

export const groupBuy = ({ orders = [], gbId = "" }) => {
  return api.post("/cpdb/group-buy", { gbId, orders });
};

export const getDeliveriesList = (limit = 10, offset = 0) => {
  return api.get(`/cpdb/deliveries-list?limit=${limit}&offset=${offset}`);
};

export const markOrderAsNotDelivered = (payload) => {
  return api.post("/cpdb/mark-cx-order-as-not-delivered", payload);
};

export const getPDG = () => {
  return api.get("/cpdb/get-pdg");
};

export const getLeaderBcard = () => {
  return api.get("/cpdb/get-bcard");
};

export const getDeliveryListCount = () => {
  return api.get("/cpdb/deliveries-list-count");
};
export const getReferralBanners = () => {
  return api.get("/cpdb/get-cl-referral-banners");
};

export const getReferedCustomers = () => {
  return api.get("/cpdb/get-cl-referred-customers");
};

export const getReferredCustomersV2 = () => {
  return api.get("/cpdb/get-cl-referred-customers-v2");
};

export const getAllOosSkus = () => api.get("/cpdb/get-oos-sku");

export const notifyForOOS = (payload) =>
  api.post("/cpdb/notify-for-oos", payload);

export const getAllBanners = () => api.get("/cpdb/banners");

// ccfp apis

export const getCCFPSummary = () => api.get("/cpdb/ccfp/summary");
export const getCCFPLandingInfo = () => api.get("/cpdb/ccfp/landing-info");
export const getCCFPPassbook = ({ offset = 0, limit = 100 }) =>
  api.get(`/cpdb/ccfp/get-all-transactions?limit=${limit}&offset=${offset}`);
export const registerCCFP = () => api.post("/cpdb/ccfp/register");
export const withdrawCFFPMoney = (payload) =>
  api.post("/cpdb/ccfp/withdraw", payload);

// wallet apis

export const getWalletOtp = (payload) =>
  api.post("/cpdb/wallet/get-otp", { ...payload });

export const validateUpi = (payload) =>
  api.post("/cpdb/wallet/validate-upi-v2", { ...payload });

export const deleteFundAccount = (payload) =>
  api.post("/cpdb/wallet/delete-fund-account-v2", payload); // new

export const createFundAccountBank = (payload) =>
  api.post("/cpdb/wallet/fund-account-v2", {
    ...payload,
  }); // new

export const withdrawMoney = (payload) =>
  api.post("/cpdb/wallet/withdraw", payload);

export const editBlackListedFundAccount = (payload) =>
  api.post("/cpdb/wallet/edit-fund-account", payload);

export const editUserFundAccount = (payload) =>
  api.post("/cpdb/wallet/edit-user-fund-account", payload);

export const createFundAccountBankOld = (payload) =>
  api.post("/cpdb/wallet/fund-account", {
    ...payload,
  });

export const deleteFundAccountOld = (payload) =>
  api.post("/cpdb/wallet/delete-fund-account", payload);

export const validateUpiOld = (payload) =>
  api.post("/cpdb/wallet/validate-upi", payload);

export const gullakCms = () => api.get("/cpdb/wallet/cms");

export const validateAmount = (payload, mode) =>
  api.post("/cpdb/wallet/validate-amount", { amount: payload, mode }); // validate

export const setPrimaryFundAccount = (payload) =>
  api.post("/cpdb/wallet/set-default", payload);

// get

export const getIfscDetails = (ifsc) =>
  api.get(`/cpdb/wallet/ifsc?ifsc=${ifsc}`); // validate
export const getFundAccounts = () => api.get("/cpdb/wallet/fund-accounts");
export const getAllWalletMetaData = () => api.get("/cpdb/wallet-metadata");

export const getUpiMetadata = () => api.get("/cpdb/wallet/wallet-meta");

export const getWalletSummary = ({ from, to }) =>
  api.get(`/cpdb/wallet-summary?fromDate=${from}&toDate=${to}`);
export const getWalletIdfaStatus = () => api.get("/cpdb/wallet-idfa-status");

export const setAppConfigsApi = (
  versionName,
  versionNumber,
  fcm_token,
  notificationPermissionStatus,
) =>
  api.post("/cpdb/set-one-signal-user-id", {
    versionName,
    versionNumber,
    fcm_token,
    notificationPermissionStatus,
  });

export const getWalletTransactionSummary = ({
  type = "",
  from,
  to,
  offset = 0,
  limit = 100,
}) =>
  api.get(
    `cpdb/wallet-transaction-by-type?fromDate=${from}&toDate=${to}&type=${type}&limit=${limit}&offset=${offset}`,
  );

export const getPassbookTransactions = ({
  from,
  to,
  offset = 0,
  limit = 100,
  wt_ids,
}) => {
  // if more then one transactions send it as comma separated wt_ids
  if (wt_ids) {
    return api.get(
      `/cpdb/all-wallet-transactions?fromDate=${from}&toDate=${to}&limit=${limit}&offset=${offset}&wt_ids=${wt_ids}`,
    );
  }
  return api.get(
    `/cpdb/all-wallet-transactions?fromDate=${from}&toDate=${to}&limit=${limit}&offset=${offset}`,
  );
};
export const resetClCache = () => api.get("/cpdb/reset-cl-cache");

export const setBotViewedItems = (payload) =>
  api.post("/botcms/viewed-items", payload);

export const getHomePage = ({ limit = 20 }) =>
  api.get(`/cms/home-page?limit=${limit}`);

export const getCLTrainingPage = () => api.get("/cms/cl-training-page");
export const oldGetCMSEntityById = ({
  id,
  limit = 10,
  offset = 0,
  root = false,
  // user_id,
  complete = false,
}) =>
  api.get(
    `/cms?id=${id}&limit=${limit}&offset=${offset}&app=cl&isRoot=${root}&bundled=yes&complete=${complete}`,
  );

// Group Buy
export const getAllGroupBuysv2 = () => api.get("/cpdb/all-group-buys-v2");

export const getAllOffersOnWa = () => api.get("/cpdb/wa-statuses");

// rating apis

export const getClReviewOptions = () => api.get("/cpdb/get-cl-review-options");

export const addRatingReview = (payload) =>
  api.post("/cpdb/add-rating-review-cl", payload);

export const addRatingReviewForOrder = (payload) =>
  api.post("/cpdb/add-rating-review-cl-for-order", payload);

// https://maaptol.in/api/cpdb/check-cl-order-rating

export const checkClRating = () => api.get("/cpdb/check-cl-order-rating-new");

export const getLocationByZipCode = (pincode) => {
  return api.get(`cpdb/get-city-from-pincode/${pincode}`);
};

export const placesAutocomplete = (input) => {
  return api.get(`cpdb/google-places/places-autocomplete?input=${input}`);
};

export const getLatLngByPlaceId = (place_id) => {
  return api.get(
    `/cpdb/google-places/address-from-placeid?placeid=${place_id}`,
  );
};

export const getAddressByLatLng = (lat, lng) => {
  return api.get(
    `/cpdb/google-places/address-from-lat-lng?lat=${lat}&lng=${lng}`,
  );
};

export const getLatLngByAddress = (add) => {
  return api.get(`/cpdb/google-places/lat-lng-from-address?address=${add}`);
};

export const setTrackingInfo = (trackingInfo) => {
  return api.post("/cpdb/tracking-info", { trackingInfo });
};

export const getSuggestedProducts = (slug) =>
  api.get(`/products/popular-product/${slug}`);

export const getAppSettings = () => api.get("/cpdb/app-settings");

export const submitPincode = (data) => api.post("/cpdb/set-pincode", data);
// Sign up as a new CL who is already a customer - with order / without order

export const getMyLeader = () => api.get("/cpdb/find-my-leader");

export const joinCLProgram = () =>
  api.post("/cpdb/remove-already-a-cx-wo-order");

// undiscard the leader..

export const undiscardMe = (data) => api.post("/cpdb/undiscard-me", data);

// profile related apis

export const getDropDownOptions = () =>
  api.get("/cpdb/cl-profile-dropdown-option-1");

export const getDropDownOptions_old = () =>
  api.get("/cpdb/cl-profile-dropdown-option");

export const updateClAddress = (data) =>
  api.post("/cpdb/update-cl-profile-address-other-details", data);

export const updateBasicInfo = (data) =>
  api.post("/cpdb/update-cl-profile-other-details", data);

// axios v1 fails to recognize React Native's FormData polyfill (its
// `instanceof FormData` check misfires on Hermes/Metro, and the polyfill
// doesn't tag itself via Symbol.toStringTag), so axios's default transform
// JSON-stringifies the FormData into {"_parts":[...]}. Even when detection
// passes, `resolveConfig` only strips Content-Type for browser envs — not
// React Native — so the boundary is never injected.
// RN's native fetch handles its own polyfill correctly, so all multipart
// uploads go through this helper instead of the axios `api` instance.
// Returns an axios-shaped { data, status } so callers don't need to change.
async function uploadMultipart(path, data) {
  const token = await getTokenZ();
  const url = `${baseURL}${path.replace(/^\/+/, "")}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: token,
      "x-app-name": "CPDB",
      "x-app-version": appVersion,
      "x-app-version-code": appVersionCode,
      ...webPlatformHeaders,
      // Intentionally NOT setting Content-Type — RN's fetch will set
      // "multipart/form-data; boundary=..." with the correct boundary.
    },
    body: data,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${path} ${res.status}: ${text}`);
  }
  return { data: await res.json().catch(() => ({})), status: res.status };
}

export const uploadImage = (data) =>
  uploadMultipart("media/upload-image", data);

export const uploadAttachments = (data) =>
  uploadMultipart("media/upload-attachments", data);

export const uploadCloudfront = (data) =>
  api.post("media/upload-cloudfront", data);

export const deleteImage = (filename) =>
  api.post("media/delete-image", { filename });

export const deleteCloudfront = (filename) =>
  api.post("media/delete-cloudfront", { filename });

export const uploadPrivateImage = (data) =>
  uploadMultipart("cpdb/upload-private-image", data);

// Onboarding variant — no leader session required. Backend's
// checkPhoneNumberExists middleware requires a cp_db_kyc_onboard row in
// PROCESSING/REJECTED status (created automatically at verify-otp).
// Caller must pass multipart FormData with fields `image` (file) and `mobile`
// (the onboarding phone number). Returns { url }.
export const uploadPrivateOnboardingImage = (data) =>
  uploadMultipart("cpdb/upload-private-onboarding-image", data);

export const uploadFaceCaptureImage = (data) =>
  uploadMultipart("cpdb/upload-face-capture-image", data);

export const deletePrivateImage = (filename) =>
  api.post("/cpdb/delete-private-image", { filename });

export const updateProfileImage = (user_image) =>
  api.post("cpdb/update-profile-image", { user_image });

export const updateLeaderName = (data) =>
  api.post("/cpdb/edit-leader-name", data);

export const submitLeaderKyc = (data) => api.post("/cpdb/submit-kyc", data);

// (v2) Diff-based resubmit — send only CHANGED kyc_items. The backend resets
// each changed item to PROCESSING regardless of prior status and re-runs only
// those checks. v1 submitLeaderKyc is kept for older app builds.
export const submitLeaderKycV2 = (data) =>
  api.post("/cpdb/v2/submit-kyc", data);

export const getLeaderKycInfo = () => api.get("/cpdb/get-kyc");

export const shouldShowKycNudge = () => api.get("/cpdb/show-kyc-nudge");

export const getAppBootParams = () => api.get("/cpdb/setup-5");

export const toggleCMAddCustomer = (radius) =>
  api.post("/cpdb/set-serviceability-radius", {
    radius,
  });

export const viewCartLite = ({ userId }) =>
  api.get(`/cpdb/view-cart-light?userId=${userId}`);

export const deliveryTimeline = () => api.get("/cpdb/delivery-timeline");
export const getAllCohorts = () => api.get("/cpdb/customer-cohorts-list");

export const getCohortById = (id) => api.get(`/cpdb/customer-cohorts/${id}`);

export const getCartCMS = (userId) =>
  api.get(`/cpdb/view-cart-cms?userId=${userId}`);

export const useCashBack = (user_id) =>
  api.post("/cpdb/use-cashback", {
    user_id,
  });

export const dontUseCashBack = (user_id) =>
  api.post("/cpdb/dont-use-cashback", {
    user_id,
  });
// import jsonData from "./view-cart-cms.json";
// export const getCartCMS = () =>
//   new Promise((resolve, reject) => resolve(jsonData));

export const payNowMakeOnlinePayment = (payementData) =>
  api.post("/db/make-online-payment", payementData);

export const payNowCompleteOnlinePayment = (payementData) =>
  api.post("/db/confirm-online-payment", payementData);

export const requestPayNowOnlinePayment = () =>
  api.get("/db/request-online-payment");

// return_replace

export const getDelayedData = () => api.get("/cpdb/delayed-setup-4");
export const getReturnReplaceOrderDetails = (order_id = "") =>
  api.get(`/cpdb/return-replace-order-details/${order_id}`);

export const returnRequest = (body) =>
  api.post("/cpdb/request-return-order-item", body);

export const replaceRequest = (body) =>
  api.post("/cpdb/request-replace-order-item", body);

// pancard api
export const getPanCardCheck = () =>
  api.get("cpdb/check-commission-for-pancard");

export const submitPanData = (body) =>
  api.post("cpdb/add-pancard-details", body);

export const getLGL = () => api.get("/cpdb/lgl/my-journey");

export const getNudge = () => api.get("/cpdb/nudges");

export const saveDeviceLocation = (body) =>
  api.post("/cpdb/profile/device-coords", body);

export const getNotificationByLeaderId = (leaderId) =>
  api.get(`/cpdb/in-app-notifications/leader-id/${leaderId}`);

export const getCLLocation = () => api.get("/cpdb/address");
export const getAgentDetails = () => api.get("/cpdb/get-ae-details");

export const submitPoll = ({ pollId, answerId }) =>
  api.post(`/cpdb/submit-poll-response/${pollId}`, {
    answerId,
  });
export const submitSubjectiveQuestionPoll = ({ pollId, subjectiveAnswer }) =>
  api.post(`/cpdb/submit-poll-response/${pollId}`, {
    subjectiveAnswer,
  });

export const getPollFormItems = (pollFormId) =>
  api.get(`/cpdb/poll-form-items/${pollFormId}`);

export const submitPollFormResponses = (data) =>
  api.post("/cpdb/submit-poll-form-responses", data);

export const getCmoCx = (lastKey = {}) => {
  return api.post("/cpdb/get-cmocx-near-cl-v3", {
    lastKey,
  });
};

export const getCmoCxSummary = () => {
  return api.get("/cpdb/cmocx-summary");
};

export const rejectCmoCx = (userId, reject_reason) => {
  return api.post("/cpdb/nahi-jodo", {
    user_id: userId,
    reject_reason,
  });
};

export const acceptCmoCx = (userId, userName) => {
  return api.post("/cpdb/cl-jodo", { user_id: userId, user_name: userName });
};
// order tracking

export const getOrderStates = (order_id) => {
  return api.get(`/cpdb/v4/order-states?order_id=${order_id}`);
};

export const reportRTO = (data) => api.post("/cpdb/v2/report-rto", data);
export const postActionCall = (data, url) => api.post(url, data);

export const markCxOrderDelivered = (data) =>
  api.post("/cpdb/mark-cx-order-as-delivered-3-single", data);

export const getPollData = (id) => {
  const url = `/cpdb/poll?pollId=${id}`;
  return api.get(url);
};

// Language
export const setLang = (data) => api.post("/cpdb/set-user-language", data);

export const getLang = () => api.get("/cpdb/get-languages");

export const fetchReturnOrders = ({
  offset = 0,
  limit = 10,
  query = "",
  date,
}) =>
  api.get(
    `/cpdb/rvp/get-returns?offset=${offset}&limit=${limit}&searchTerm=${query}&date=${date}`,
  );

export const fetchReturnOrdersV2 = ({
  offset = 0,
  limit = 10,
  query = "",
  date,
}) =>
  api.get(
    `/cpdb/rvp/get-returns-v2?offset=${offset}&limit=${limit}&searchTerm=${query}&date=${date}`,
  );

export const returnRequestCancel = ({ return_req_id }) =>
  api.post(`/cpdb/rvp/cancel/${return_req_id}`);

export const returnRequestCancelV2 = ({ return_req_id, type }) =>
  api.post("/cpdb/rvp/cancel-v2", { return_req_id, type });

export const scheduleRVPPickupDate = (data) =>
  api.post("/cpdb/rvp/schedule", data);

// return & redund flow related apis
export const getRvpOrderDetails = (order_id) => {
  return api.get(`cpdb/rvp/order-v2/${order_id}`);
};

export const submitRvpTicket = (data) => {
  return api.post("cpdb/rvp/submit", data);
};

export const submitRvpTicketMultiple = (data) => {
  return api.post("cpdb/rvp/submit/multiple", data);
};

// Sales Report

export const getSales = (selectedMonth) => {
  return api.get(`us/sales-report/get-overall-report?aggDate=${selectedMonth}`);
};

export const deactivateAccount = (payload) =>
  api.post("cpdb/deactivate-leader-state", payload);

export const checkReactivationReason = () =>
  api.get("cpdb/check-cl-reactivation-data");

export const setReactivationReason = (payload) => {
  api.post("cpdb/reactivate-cl-data", payload);
};
// My Shop

export const getShopStats = ({ id, date }) => {
  return api.get(`/us/my-shop/get-sales-overview?id=${id}&aggDate=${date}`);
};

export const getUpdates = ({ limit = 10, offset = 0 }) => {
  return api.get(
    `/us/my-shop/get-real-time-data-v2?limit=${limit}&offset=${offset}`,
  );
};

export const getLastMonthsOverview = (currMonth) => {
  return api.get(
    `us/sales-report/get-last-6-months-overview?aggDate=${currMonth}`,
  );
};

export const getApplinkData = (hash) => api.get(`/cpdb/v1/applinks/${hash}`);

export const getClTaskPrgressData = () =>
  api.get("/cpdb/cl-tasks/get-cl-todays-tasks");

export const getClTaskSummary = () =>
  api.get("/cpdb/cl-tasks/todays-task-summary");
export const getFeedbackReasonClTask = ({ subtype = "promotion" }) =>
  api.get(`/cpdb/cl-tasks/reasons/${subtype}`);

export const getBusinessTips = ({ subtype = "promotion" }) =>
  api.get(`/cpdb/cl-tasks/todays-tips-and-status/${subtype}`);

export const getCartTips = () =>
  api.get("/cpdb/cl-tasks/todays-tips-and-status/abandoned-cart");

export const getTaskData = ({ subtype }) =>
  api.get(`/cpdb/cl-tasks/todays-details/${subtype}`);

export const postTaskDone = ({ data, subtype, id }) =>
  api.post(`/cpdb/cl-tasks/mark-task-done/${subtype}/${id}`, data);

export const reportSupport = (data) => {
  return api.post("/support/v2/cl/report", data);
};

export const getDailyDeliveries = () => {
  return api.get("us/daily-deliveries/get-daily-deliveries");
};

export const getNextDeliveries = () => {
  return api.get("us/daily-deliveries/get-next-deliveries");
};

// My Deliveries
export const getTodaysCrateDeliveries = () => {
  return api.get(
    "/cpdb/daily-deliveries-v3/overview-v3?dataType=TODAY&noOfDays=1",
  );
};

export const getPreviousCrateDeliveries = () => {
  return api.get("/cpdb/daily-deliveries-v3/group?dataType=PAST&noOfDays=7");
};

export const getUpcomingDeliveries = () => {
  return api.get("/cpdb/daily-deliveries-v3/group?dataType=FUTURE&noOfDays=7");
};

export const getCrateDeliveryDetails = ({
  date,
  spokeName = "",
  type = "NOT_ASSIGNED",
}) => {
  return api.get(
    `/cpdb/daily-deliveries-v3/daily-deliveries?type=${type}&date=${date}&spokeName=${spokeName}`,
  );
};

export const getDeliveryOrderItems = (data) => {
  return api.post(`/cpdb/daily-deliveries-v3/cp-get-cx-items-v3`, data);
};

export const itemsQuantityStatusV2 = (data) => {
  return api.post(`/cpdb/daily-deliveries-v3/v2/cp-pack-cx-item`, data);
};

export const itemsQuantityStatusV3 = (data) => {
  return api.post(`/cpdb/daily-deliveries-v3/cp-pack-cx-item-v3`, data);
};

export const markFoundStatus = ({ order_id, order_item_id, ofd_date }) => {
  return api.post(`/cpdb/daily-deliveries-v3/found-mark-item`, {
    order_id,
    order_item_id,
    ofd_date,
  });
};

export const itemsAddedInBag = (data) => {
  return api.post(`/cpdb/daily-deliveries-v3/cp-submit-cx`, data);
};

export const itemsQuantityStatus = (data) => {
  return api.post(`/cpdb/daily-deliveries-v3/cp-pack-cx-item`, data);
};

export const getComboItems = (data) => {
  return api.post(`/cpdb/daily-deliveries-v3/cp-get-cx-combo-items`, data);
};

export const getIssuesList = ({ date, spoke_name }) => {
  return api.get(
    `/cpdb/daily-deliveries-v3/get-issues-list?date=${date}&spoke_name=${spoke_name}`,
  );
};

export const submitComboItems = (data) => {
  return api.post(`/cpdb/daily-deliveries-v3/cp-pack-cx-combo-item`, data);
};

export const getDailyRTOSV3 = ({ date, spokeName = "" }) => {
  return api.get(
    `/cpdb/daily-deliveries-v3/daily-rtos?date=${date}&spokeName=${spokeName}`,
  );
};

export const getReturnDetailsV3 = ({ date, spokeName = "" }) => {
  return api.get(
    `/cpdb/daily-deliveries-v3/daily-returns?date=${date}&spokeName=${spokeName}`,
  );
};

export const getRecentLedgerTxns = (limit = 1000, latestId) => {
  let uri = `/cpdb/daily-deliveries-v3/recent-ledger-txns?limit=${limit}`;
  if (latestId) {
    uri += `&latestId=${latestId}`;
  }
  return api.get(uri);
};
export const getRecentLedgerTxnsv2 = ({ payload }) => {
  let uri = `/cpdb/daily-deliveries-v3/recent-ledger-txns-v2`;
  return api.post(uri, payload);
};

export const getLedgerItemModalData = ({ modal_api }) => {
  return api.get(modal_api);
};

export const setContestResultViewed = (data) =>
  api.post("cl-contest/cpdb/contest-result-viewed", data);

export const contestSearchTerm = ({ contest_id = 1, searchTerm = "" }) =>
  api.get(
    `cl-contest/cpdb/contest-cx?contestId=${contest_id}&searchTerm=${searchTerm}`,
  );
export const getEarlyJourneyQuestionaire = () => {
  return api.get("/cpdb/early-journey-questions");
};

export const answerEarlyJourneyQuestionaire = (payload) => {
  return api.post("/cpdb/early-journey-questions/answer", payload);
};

export const earlyJourneyUpdateShopName = (payload) =>
  api.post("/cpdb/early-journey-update-shop-name", payload);
export const getRvpCXRTO = (orderId) => {
  return api.get(`cpdb/rvp/cx-rto-page/${orderId}`);
};

export const getCtareRvpCXRTO = (orderId) => {
  return api.get(`cpdb/rvp/crate-cx-rto-page/${orderId}`);
};

export const createCXRTO = ({ order_id = "", reason = "" }) =>
  api.post("cpdb/rvp/create-cx-rto", { order_id, reason });

export const crateCreateCXRTO = ({
  order_id = "",
  reason = "",
  lat = null,
  lng = null,
}) => api.post("cpdb/rvp/crate-create-cx-rto", { order_id, reason, lat, lng });

// Deliveries v2
export const getDeliveriesOverview = () => {
  return api.get("/cpdb/mark-deliveries-v3/widget");
};
export const getDeliveryReturn = () => {
  return api.get("/cpdb/my-deliveries-v2/deliveries-pickups");
};
export const getRefunds = () => {
  return api.get("/cpdb/my-deliveries-v2/refunds");
};

export const rescheduleDelivery = (user_id, order_id, action_name) => {
  return api.post("/cpdb/my-deliveries-v2/reschedule-delivery", {
    user_id,
    order_id,
    action_name,
  });
};

export const markPickup = (return_req_id, otp) => {
  return api.post(`/cpdb/rvp/mark-picked-from-cx/${return_req_id}`, { otp });
};
export const cancelPickupReq = (return_req_id, otp, type = "rvp") =>
  api.post("/cpdb/rvp/cancel-v2", { otp, return_req_id, type });

export const unMarkPickup = (return_req_id) => {
  return api.post(`/cpdb/rvp/unmark-picked-from-cx/${return_req_id}`);
};

export const markDelivery = (order_id, otp, lat, lng) => {
  return api.post("/cpdb/mark-cx-order-as-delivered-3-single", {
    order_id,
    otp,
    lat,
    lng,
  });
};
export const rejectDelivery = (id, type = "cx_rto") => {
  return api.post("/cpdb/rvp/cancel-v2", { return_req_id: id, type });
};
export const unMarkDelivery = (order_id) => {
  return api.post("/cpdb/unmark-cx-order-as-delivered-2-single", {
    order_id,
  });
};

export const markRefunded = (return_req_id, otp) => {
  return api.post(`/cpdb/mark-refunded-to-cx/${return_req_id}`, { otp });
};
export const unMarkRefunded = (return_req_id) => {
  return api.post(`/cpdb/unmark-refunded-to-cx/${return_req_id}`);
};

export const markAction = (action) => {
  return api.post("/cpdb/mark-action", { action });
};

export const markMyDeliveyModalAction = (action) => {
  return api.post("/cpdb/mark-my-delivery-info-modal-action", { action });
};

export const getInfoDeliveryFlow = () => api.get("/cpdb/delivery-flow/info");
export const confirmOrderDeliveryFlow = (data = {}) =>
  api.post("/cpdb/delivery-flow/confirm-order", data);

export const fetchPerformanceMetricsOverview = () =>
  api.get("/cpdb/order-metrics/overview");

export const fetchPerformanceMetricsData = (type) =>
  api.get(`/cpdb/order-metrics/detailed?type=${type}`);

export const fetchDeliveryRating = (month) =>
  api.get(`/cpdb/partner-performance/delivery-rating?month=${month}`);

export const fetchCustomerComplaints = () =>
  api.get(`/cpdb/partner-performance/customer-complaints`);

export const getDeliveryDetailsOverview = (payload) =>
  api.post("/cpdb/daily-deliveries-details-v3/get-delivery-details", payload);

export const getReturnDetailsOverview = (payload) =>
  api.post("/cpdb/daily-deliveries-details-v3/get-return-details", payload);

export const getOverviewItemDetails = ({ type, id }) =>
  api.get(
    `/cpdb/daily-deliveries-details-v3/get-item-details?type=${type}&id=${id}`,
  );

export const markCxFraud = (payload) =>
  api.post("/cpdb/mark-cx-fraud", payload);

export const getAllCpDbs = () => api.get("/cpdb/dbs-v2/all");

export const addCpDb = (payload) => api.post("/cpdb/dbs-v2/add", payload);

export const deleteCpDb = ({ db_id, overide = false }) =>
  api.put(`/cpdb/dbs-v2/disable/${db_id}?override=${overide}`);

export const getCpDbRoutes = ({ db_id = "ALL" }) =>
  api.get(`/cpdb/dbs/routes?dbId=${db_id}`);

export const assignCpDbBulk = (payload) =>
  api.post("/cpdb/dbs/assign-db-bulk", payload);

export const unassignDb = (payload) =>
  api.post("/cpdb/dbs/unassign-db", payload);

export const switchDbDeliveries = (payload) =>
  api.post("/cpdb/dbs/switch-db-bulk", payload);

export const getDailyCratesItems = ({ type, date }) =>
  api.get(
    `/cpdb/daily-deliveries-v3/daily-crate-items?type=${type}&date=${date}`,
  );

export const getOrderUsersList = (payload) =>
  api.post("/cpdb/daily-deliveries-v3/users-list-v2", payload);

export const scanCrate = ({ crate_id, type, payload }) =>
  api.put(
    `/cpdb/daily-deliveries-v3/crate-scan-submit/${crate_id}?type=${type}`,
    payload,
  );

export const getRouteList = () => api.get("/cpdb/dbs-v2/routes-list");

export const addDBRoute = (payload) =>
  api.post("/cpdb/dbs-v2/create-route", payload);

export const deleteDBRoute = (payload) =>
  api.post("/cpdb/dbs-v2/delete-route", payload);

export const updateDBRoute = (payload) =>
  api.post("/cpdb/dbs-v2/update-route", payload);

export const getDbTouchpoints = ({ route_id = "ALL", type = "TODAY" }) =>
  api.get(`/cpdb/dbs-v2/touchpoints?type=${type}&route_id=${route_id}`);

export const assignTouchpointBulk = (payload) =>
  api.post("/cpdb/dbs-v2/assign-touchpoint-bulk", payload);

export const assignTouchpoint = (payload) =>
  api.post("/cpdb/dbs-v2/assign-touchpoint", payload);

export const addToBag = (payload) =>
  api.post("/cpdb/daily-deliveries-v3/add-to-bag", payload);

export const getDeliveryTasks = (payload) =>
  api.post("/cpdb/mark-deliveries-v3/delivery-tasks-v3", payload);

export const getPickupItemsListV2 = (payload) =>
  api.post("/cpdb/mark-deliveries-v3/get-items-list-v2", payload);

export const createSarvCallAPI = (payload) =>
  api.post("/cpdb/mark-deliveries-v3/click-to-call", payload);

export const getBrokenCalls = (page = 1, limit = 20) =>
  api.get(`/cpdb/broken-calls?page=${page}&limit=${limit}`);

export const getDeliveryTasksMap = (payload) =>
  api.post("/cpdb/mark-deliveries-v3/delivery-tasks-map-v2", payload);

export const getUserOrders = ({
  user_id,
  data_type = "TODAY",
  db_id = "NOT_ASSIGNED",
  route_id = "NOT_ASSIGNED",
  trip_id = null,
}) => {
  // BI-17. trip_id is APPENDED, never substituted, so a caller that passes
  // nothing builds byte-identically to the pre-BI-17 URL and a backend that
  // does not yet read the param is unaffected. Values the backend accepts:
  //   <uuid>    → only that trip's orders
  //   PENDING   → only orders that have never been given a trip
  //   (omitted) → today's behaviour, unscoped
  const base = `/cpdb/mark-deliveries-v3/delivery-tasks-by-cx-id-v3/${user_id}?data_type=${data_type}&db_id=${db_id}&route_id=${route_id}`;
  return api.get(trip_id ? `${base}&trip_id=${trip_id}` : base);
};

export const markTaskActions = (payload) =>
  api.post(`/cpdb/mark-deliveries-v3/mark-task-actions-v2`, payload);

export const resendRescheduleOtp = (payload) =>
  api.post(`/cpdb/mark-deliveries-v3/resend-reschedule-otp`, payload);

export const markMultipleDeliveries = (payload) =>
  api.post("/cpdb/mark-deliveries-v3/cp-cx-complete-delivery", payload);

export const cancelRto = (payload) =>
  api.post("/cpdb/rvp/cancel-v2-multiple", payload);

// OTP request
export const submitOtpRequest = (payload) =>
  api.post("/cpdb/otp-requests/submit", payload);

// homepage widgets
export const getHomePageWidgets = () => api.get("/cpdb/v2/homepage/widgets");
export const getHomePageWidgetData = () =>
  api.get("/cpdb/v2/homepage/widgets-data");

// db management
export const getDBManageOverview = () => api.get("/cpdb/db-manage/overview");
export const getOutForDeliveryDb = () => api.get("/cpdb/db-manage/ofd-dbs");
export const getOutForDeliveryDbDetails = ({
  db_id,
  route_id = "ALL",
  search_term = "",
}) => {
  return api.get(
    `cpdb/db-manage/ofd-db-by-id/${db_id}?route_id=${route_id}&search_term=${search_term}`,
  );
};
export const markOutForDelivery = ({ payload, db_id }) =>
  api.post(`/cpdb/db-manage/ofd-mark-bulk-cx/${db_id}`, payload);

export const getRoutingList = () => api.get("/cpdb/db-manage/routing-details");

// recon
export const getReconDbs = () => api.get("cpdb/db-manage/recon-dbs");
export const getReconByDbId = ({ db_id, search_term = "" }) =>
  api.get(`/cpdb/db-manage/recon-db-by-id/${db_id}?search_term=${search_term}`);

export const submitReconCollectedCash = ({ payload, db_id }) =>
  api.post(`/cpdb/db-manage/recon-submit-collected-cash/${db_id}`, payload);

export const getReconHistory = () => api.get("/cpdb/db-manage/recon-history");

export const getReconHistoryByDbId = ({ db_id, ofd_date, search_term }) =>
  api.get(
    `/cpdb/db-manage/recon-history-by-id/${db_id}/${ofd_date}?search_term=${search_term}`,
  );

export const createOnlinePayment = (payload) =>
  api.post("/cpdb/create-payment", payload);
export const completeOnlinePayment = (payload) =>
  api.post("/cpdb/complete-payment", payload);

export const createOnlinePaymentV2 = (payload) =>
  api.post("/cpdb/create-payment-v2", payload);
export const completeOnlinePaymentV2 = (payload) =>
  api.post("/cpdb/complete-payment-v2", payload);

export const markItemLostDamaged = (payload) =>
  api.post("/cpdb/daily-deliveries-v3/cp-mark-item-lost-damage", payload);

export const markReconSubmit = ({ db_id, payload }) =>
  api.post(`/cpdb/db-manage/recon-mark-as-submitted/${db_id}`, payload);

export const getDeliveryPaymentTxns = ({ spoke_name }) =>
  api.get(
    `/cpdb/daily-deliveries-v3/delivery-payment-txns?spoke_name=${spoke_name}`,
  );

export const getDailyPerformance = (date) =>
  api.get(`/cpdb/v2/cp-performance/daily-performance?date=${date}`);

export const getWeeklyPerformance = (fromDate, toDate) =>
  api.get(
    `/cpdb/v2/cp-performance/weekly-performance?fromDate=${fromDate}&toDate=${toDate}`,
  );
export const getWeekDayPerformance = (date, fromDate, toDate) =>
  api.get(
    `/cpdb/v2/cp-performance/weekday-performance?date=${date}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const markTouchpointPickedUpByCpdb = (payload) =>
  api.post(`/cpdb/mark-deliveries-v3/pick-cx-items`, payload);

export const markTouchpointPickedUpByCpdbV2 = (payload) =>
  api.post(`/cpdb/mark-deliveries-v3/pick-cx-items-v2`, payload);

export const unmarkPickupByCpdbV2 = (payload) =>
  api.post(`/cpdb/mark-deliveries-v3/unmark-pickup-v2`, payload);

// Dark-store rack-based picking (pickup_scan_v3_enabled CPs).
// See docs/cp-features/darkstore-api-contract.md §3 in citymall-services.
export const getPickShipmentsV3 = (payload) =>
  api.post(`/cpdb/mark-deliveries-v3/get-pick-shipments-v3`, payload);

export const pickTpV3 = (payload) =>
  api.post(`/cpdb/mark-deliveries-v3/pick-tp-v3`, payload);

/* wallet v2 */
export const getWalletSummaryV2 = ({
  fromDate = "",
  toDate = "",
  type = "",
  TIME = "DEFAULT",
}) =>
  api.get(
    `/cpdb/v2/wallet/summary?type=${type}&TIME=${TIME}&start_date=${fromDate}&end_date=${toDate}`,
  );

export const getWalletMetaDataV2 = () => api.get(`/cpdb/v2/wallet/metadata`);

export const getWalletTransactionsV2 = ({
  fromDate = "",
  toDate = "",
  type = "",
  TIME = "DEFAULT",
  TRANSACTION = "TXN_DEFAULT",
  limit = 20,
  offset = 0,
}) =>
  api.get(
    `/cpdb/v2/wallet/transactions?type=${type}&TIME=${TIME}&TRANSACTION=${TRANSACTION}&start_date=${fromDate}&end_date=${toDate}&limit=${limit}&offset=${offset}`,
  );
export const raiseDispute = (data) => api.post(`/cpdb/raise-dispute`, data);

export const getTransactionDispute = (txn_id) =>
  api.get(`/cpdb/fetch-dispute?txn_id=${txn_id}`);

export const cancelDispute = (data) =>
  api.post(`/cpdb/cancel-dispute?txn_id=${data.txn_id}`);

export const getTodaysReturns = ({ date, spokeName = "" }) => {
  return api.get(
    `/cpdb/daily-deliveries-v3/sku-wise-returns-v2?date=${date}&spokeName=${spokeName}`,
  );
};

export const getBagsItemsData = ({
  order_ids,
  user_id,
  plastic_bag_opted_in,
}) =>
  api.post(`/cpdb/mark-deliveries-v3/get-items-list`, {
    order_ids,
    user_id,
    plastic_bag_opted_in,
  });

export const getDeliveryOtpViaSms = (
  phone_number,
  order_id,
  payable_amount,
  send_cancel_delivery_otp = false,
) => {
  return api.post(`/cpdb/get-delivery-otp-via-sms`, {
    phone_number,
    order_id,
    payable_amount,
    send_cancel_delivery_otp,
  });
};

export const createQrCodePayment = (
  order_ids = [],
  cx_id = "",
  payable_amount = 0,
) => {
  return api.post(`/cpdb/create-qr-payment`, {
    order_ids,
    cx_id,
    payable_amount,
  });
};
export const getQrCodeStatus = ({
  qrOrderId = "",
  otp_enabled = false,
  cx_id = "",
  order_ids = {},
}) => {
  return api.post(
    `/cpdb/get-qr-status?qr_order_id=${qrOrderId}&otp_enabled=${otp_enabled}&cx_id=${cx_id}`,
    { order_ids },
  );
};

export const getReturnDetail = (payload) =>
  api.post(`/cpdb/rvp/dashboard-details`, payload);

export const sendQrPaymentOnWhatsApp = (qrOrderId, cxPhone) =>
  api.post(`/cpdb/qr-payment/send-whatsapp`, {
    qr_order_id: qrOrderId,
    cx_phone: cxPhone,
  });

export const getPlanModalData = (cta_api = "") => {
  return api.get(cta_api);
};

//websocket apis

export const initializeWebSocketSession = async () => {
  const userToken = await getTokenZ();
  return {
    service: "partner_ws",
    route: "start_session",
    auth_token: userToken,
    payload: "start session",
  };
};

export const testRefreshButton = async () => {
  const userToken = await getTokenZ();
  return {
    service: "partner_ws",
    route: "button_refresh",
    auth_token: userToken,
    payload: "refresh button clicked",
  };
};
// type = CREDIT, DEBIT
export const getTransactionDetails = (id, type) =>
  api.get(
    `/cpdb/daily-deliveries-v3/credit-debit-and-crate-details?id=${id}&type=${type}`,
  );

export const getWalletSummaryV3 = () => api.get(`/cpdb/v3/wallet/summary`);

export const getReconInfo = (date = "") =>
  api.get(`/cpdb/v2/cp-performance/recon-info?date=${date}`);

export const getWalletTransactions = (type = "WITHDRAWAL", date_range = "") => {
  return api.get(
    `/cpdb/v3/wallet/transactions?type=${type}&date_range=${date_range}`,
  );
};

export const getTripHistory = (payload) => {
  return api.post("/cpdb/v3/wallet/transaction-history", payload);
};

export const getDailyEarnings = (date = "") => {
  return api.get(`/cpdb/v3/wallet/daily-earnings?date=${date}`);
};

export const getWeeklyEarnings = (dateRange = "") => {
  return api.get(`/cpdb/v3/wallet/weekly-earnings?date_range=${dateRange}`);
};
export const getTransactionStatement = ({ date_range, type }) =>
  api.get(
    `/cpdb/v3/wallet/transaction-statement?date_range=${date_range}&type=${type}`,
  );

export const getWalletTransactionDetails = (id) =>
  api.get(`/cpdb/v3/wallet/transaction-details?txn_id=${id}`);

export const getWalletDetailedTransactions = (ids = []) =>
  api.post("/cpdb/v3/wallet/detailed-transactions", { ids });
